import './App.css';
import {
  BrowserRouter,
  Outlet,
  Route,
  Routes,
  useLocation,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import Navbar from './components/nav/navbar.component';
import ErrorPage from './components/error/error.page';
import { CompanySettingsPage } from './components/setting/company-settings.page';
import InboxPage from './components/inbox/inbox.page';
import { LoginPage } from './components/login/login.page';
import jwt_decode from 'jwt-decode';
import { logout } from './utils/apiUtils';
import { InboxView } from './components/inbox/InboxView';
import { ConfigProvider } from './components/common/config-context/ConfigContext';
import { SubscriptionProvider } from './components/common/subscription-context/SubscriptionContext';
import DashboardPage from './components/dashboard/dashboard.page';
import MonitorsPage from './components/monitors/monitors.page';
import StaffPage from './components/staff/staff.page';
import MonitorPage from './components/monitor/monitor.page';
import ConditionPage from './components/condition/condition.page';
import SitePage from './components/sites/site/site.page';
import SitesPage from './components/sites/sites.page';
import { ResetPasswordPage } from './components/reset-password/reset-password.page';
import { ProfileSettingsPage } from './components/setting/profile-settings.page';
import 'react-toastify/dist/ReactToastify.css';
import FleetsPage from './components/fleet/fleets.page';
import { SocketProvider } from "./components/common/socket-context/SocketContext";
import JobsPage from "./components/jobs/jobs.page";
import JobPage from "./components/jobs/job.page";
import MapPage from './components/map/map.page';
import { toast, ToastContainer } from "react-toastify";
import { CheckBadgeIcon } from "@heroicons/react/24/outline";
import DevicePage from './components/sites/site/device/device.page';
import { AppLinksPage } from './components/applinks/applinks.page';
import WelcomePage from './pages/welcome.page';
import RegisterOrganizationPage from './pages/register-organization.page';
import RegisterAdminPage from './pages/register-admin.page';
import RegisterPaymentPage from './pages/register-payment.page';
import PastDuePaymentPage from './pages/past-due-payment.page';
import SubscriptionStatusGuard from './components/common/SubscriptionStatusGuard';

export function toastSuccess(message: string) {
  toast.success(message, {
    icon: <CheckBadgeIcon color="rgb(45 212 191)" />,
    progressStyle: { background: "rgb(45 212 191)" }
  });
}

export function toastError(message: string) {
  toast.error(message);
}

function App() {
  return (
    <>
      <BrowserRouter>
        <SubscriptionProvider>
          <Routes>
            <Route
              path="/"
              element={
                <SocketProvider>
                  <PrivateRoutes
                    userType={'MANAGER'}
                    component={
                      <ConfigProvider>
                        <LayoutsWithNavbar></LayoutsWithNavbar>
                      </ConfigProvider>
                    }
                  ></PrivateRoutes>
                </SocketProvider>
              }
            >
            <Route path="/" element={<DashboardPage />} />

            <Route path="/monitors" element={<MonitorsPage />} />
            <Route path="/monitors/:monitorId" element={<MonitorPage />} />
            <Route path="/monitors/add" element={<MonitorPage />} />
            <Route path="/monitors/:monitorId/conditions/:conditionId" element={<ConditionPage />} />
            <Route path="/monitors/:monitorId/conditions/add" element={<ConditionPage />} />

            <Route path="/sites" element={<SitesPage />} />
            <Route path="/sites/add" element={<SitePage />} />
            <Route path="/sites/:siteId" element={<SitePage />} />
            <Route path="/sites/:siteId/devices/:deviceId" element={<DevicePage />} />

            <Route path="/monitors" element={<MonitorPage />} />
            <Route path="/staff" element={<StaffPage />} />

            <Route path="/fleets" element={<FleetsPage />} />

            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/jobs/:jobId" element={<JobPage />} />

            <Route path="/map" element={<MapPage />} />

            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/inbox/:conversationId" element={<InboxView />} />

            <Route path="/setting/company" element={<CompanySettingsPage />} />
            <Route path="/setting/profile" element={<ProfileSettingsPage />} />
            <Route path="*" element={<ErrorPage></ErrorPage>} />
          </Route>

          <Route path="/login" element={<LoginPage />} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/register-org" element={<RegisterOrganizationPage />} />
          <Route path="/register-admin" element={<RegisterAdminPage />} />
          <Route path="/register-payment" element={<RegisterPaymentPage />} />
          <Route path="/past-due-payment" element={<PastDuePaymentPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/applinks" element={<AppLinksPage />} />
          <Route path="/set-password" element={<ResetPasswordPage />} />
        </Routes>
        <ToastContainer />
        </SubscriptionProvider>
      </BrowserRouter>
    </>
  );
}

function LayoutsWithNavbar() {
  return (
    <>
      <SubscriptionStatusGuard />
      <div className="sticky top-0 z-[1000]">
        <Navbar></Navbar>
      </div>

      {/* This Outlet is the place in which react-router will render your components that you need with the navbar */}
      <Outlet />

      {/* You can add a footer to get fancy in here :) */}
    </>
  );
}

const PrivateRoutes = ({ userType, component }: { userType: string, component: JSX.Element }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('accessToken');
  const hasLoggedInBefore = localStorage.getItem('hasLoggedInBefore');
  const pastDueFlag = localStorage.getItem('pastDueFlag');
  let authenticated = false;

  if (token) {
    const decoded: any = jwt_decode(token || '');
    if (decoded?.userType === userType) {
      const currentTime = Date.now() / 1000;
      if (decoded?.exp < currentTime) {
        logout();
        navigate(hasLoggedInBefore ? '/login' : '/welcome');
      } else {
        authenticated = true;
      }
    }
  }

  // If authenticated but subscription is past due, block access entirely
  if (authenticated && pastDueFlag === 'true') {
    return <Navigate to="/past-due-payment" replace />;
  }

  // If user has logged in before, go to login screen, otherwise welcome screen
  const redirectPath = hasLoggedInBefore ? '/login' : '/welcome';

  return authenticated
    ? component
    : <Navigate to={redirectPath} replace state={{ from: location }} />;
}


export default App;
